/**
 * CoverageEntityDetails - Detailed view for a Coverage entity
 *
 * Displays all fields from CoverageEntitySchema with:
 * - Change highlighting for affected fields
 * - Support for editable mode
 * - Proper formatting for enums
 * - List sections: policy identifiers, named insured, adjusters, claims, benefits
 */

'use client';

import { Shield } from 'lucide-react';
import React, { useMemo } from 'react';

import { CarrierDivisionLink } from '@/components/entity-links';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { CoverageEntitySchema } from '@/services/api/generated/entitySyncService';

import {
  FieldRow,
  formatEnumValue,
  getSuggestedValue,
  isFieldAffected,
} from './EntityFieldComponents';
import { COVERAGE_FIELD_CONFIGS, toFieldDefinitions } from './fieldConfigs';
import type { FieldDefinition } from './types';

// ============================================================================
// Types
// ============================================================================

export interface CoverageEntityDetailsProps {
  /** The coverage entity to display. Optional for CREATE scenarios. */
  entity?: CoverageEntitySchema | null;
  /** Set of field names that have suggested changes */
  affectedFields?: Set<string>;
  /** Payload containing field updates to apply */
  suggestedPayload?: CoverageEntitySchema;
  /** Whether fields should be editable */
  editable?: boolean;
  /** Callback when a field value changes */
  onPayloadChange?: (
    fieldName: keyof CoverageEntitySchema,
    value: string | null,
  ) => void;
}

// ============================================================================
// Constants
// ============================================================================

/** Core field definitions for coverage, derived from centralized config */
const CORE_FIELD_DEFINITIONS: FieldDefinition[] = toFieldDefinitions(
  COVERAGE_FIELD_CONFIGS,
  {
    carrier_division_id: {
      renderCustomValue: (value) => (
        <CarrierDivisionLink entityId={value} className="text-sm">
          Carrier Directory
        </CarrierDivisionLink>
      ),
    },
  },
);

// ============================================================================
// Sub-components
// ============================================================================

/** List section for simple string arrays */
const StringListSection: React.FC<{
  title: string;
  items: string[] | undefined;
}> = ({ title, items }) => {
  if (!items || items.length === 0) return null;

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-zinc-500">{title}</p>
      <div className="flex flex-wrap gap-1">
        {items.map((item, idx) => (
          <Badge key={idx} variant="outline" className="text-xs">
            {item}
          </Badge>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const CoverageEntityDetails: React.FC<CoverageEntityDetailsProps> = ({
  entity,
  affectedFields,
  suggestedPayload,
  editable,
  onPayloadChange,
}) => {
  const isCreate = !entity;
  const affectedSet = affectedFields ?? new Set<string>();
  const payload = suggestedPayload as Record<string, unknown> | undefined;

  const fieldRows = useMemo(() => {
    const rows: FieldDefinition[][] = [];
    for (let i = 0; i < CORE_FIELD_DEFINITIONS.length; i += 2) {
      rows.push(CORE_FIELD_DEFINITIONS.slice(i, i + 2));
    }
    return rows;
  }, []);

  return (
    <div className="space-y-4">
      {/* Coverage header card */}
      {(entity || suggestedPayload) && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-zinc-200 bg-white">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-violet-100">
            <Shield className="h-5 w-5 text-violet-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="truncate text-lg font-semibold text-zinc-900">
              {entity?.carrier_name ??
                suggestedPayload?.carrier_name ??
                'New Coverage'}
            </h4>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {(entity?.category ?? suggestedPayload?.category) && (
                <Badge variant="secondary">
                  {formatEnumValue(
                    entity?.category ?? suggestedPayload?.category ?? null,
                  )}
                </Badge>
              )}
              {(entity?.policy_type ?? suggestedPayload?.policy_type) && (
                <Badge variant="outline" className="text-xs">
                  {formatEnumValue(
                    entity?.policy_type ??
                      suggestedPayload?.policy_type ??
                      null,
                  )}
                </Badge>
              )}
              {(entity?.status ?? suggestedPayload?.status) && (
                <Badge variant="outline" className="text-xs bg-zinc-50">
                  {formatEnumValue(
                    entity?.status ?? suggestedPayload?.status ?? null,
                  )}
                </Badge>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Core field details grid */}
      <div className="space-y-3">
        <p className="text-xs font-medium uppercase tracking-wide text-zinc-400">
          Coverage Details
        </p>
        <div className={editable ? 'divide-y divide-zinc-100' : 'space-y-2'}>
          {fieldRows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className={cn(
                'grid gap-4',
                row.length === 2 ? 'grid-cols-2' : 'grid-cols-1',
                editable && 'py-2 first:pt-0 last:pb-0',
              )}
            >
              {row.map((fieldDef) => {
                const currentValue = fieldDef.getValue(entity);
                const suggested = getSuggestedValue(payload, fieldDef.key);
                const hasChange = isFieldAffected(affectedSet, fieldDef.key);

                return (
                  <FieldRow
                    key={fieldDef.key}
                    fieldDef={fieldDef}
                    currentValue={currentValue}
                    suggestedValue={suggested}
                    hasChange={hasChange}
                    isCreate={isCreate}
                    editable={editable ?? false}
                    onValueChange={
                      onPayloadChange && fieldDef.isEditable
                        ? (value) =>
                            onPayloadChange(
                              fieldDef.key as keyof CoverageEntitySchema,
                              value,
                            )
                        : undefined
                    }
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Named Insured */}
      <StringListSection
        title="Named Insured"
        items={entity?.named_insured}
      />

      {/* Policy Identifiers */}
      {entity?.policy_identifiers && entity.policy_identifiers.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-zinc-500">
            Policy Identifiers
          </p>
          <div className="space-y-1">
            {entity.policy_identifiers.map((pi, idx) => (
              <div key={idx} className="text-sm text-zinc-700">
                <span className="font-medium">
                  {formatEnumValue(pi.identifier_type)}:
                </span>{' '}
                {pi.identifier_value}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Adjusters */}
      {entity?.adjusters && entity.adjusters.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-zinc-500">Adjusters</p>
          <div className="space-y-1">
            {entity.adjusters.map((adj, idx) => (
              <div
                key={idx}
                className="text-sm text-zinc-700 rounded-md bg-zinc-50 px-3 py-2"
              >
                <span className="font-medium">{adj.name}</span>
                {adj.benefit_type && (
                  <Badge
                    variant="outline"
                    className="ml-2 text-[10px] px-1.5 py-0"
                  >
                    {formatEnumValue(adj.benefit_type)}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Claims */}
      {entity?.claims && entity.claims.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-zinc-500">Claims</p>
          <div className="space-y-1">
            {entity.claims.map((claim, idx) => (
              <div
                key={idx}
                className="text-sm text-zinc-700 rounded-md bg-zinc-50 px-3 py-2"
              >
                {claim.claim_identifier && (
                  <span className="font-medium">{claim.claim_identifier}</span>
                )}
                {claim.benefit_type && (
                  <Badge
                    variant="outline"
                    className="ml-2 text-[10px] px-1.5 py-0"
                  >
                    {formatEnumValue(claim.benefit_type)}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CoverageEntityDetails;
